import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { useWindowResize } from '../../hooks/useWindowResize'
import { Block } from '../../models/Block'
import {
    Bookable,
    getBookableColor,
    getBookableMultiDayLabel,
    getBookableStatus,
    getBookableSubtitle,
    getBookableTimeLabel,
    getBookableTitles,
    isFixedEventReservationItem,
    isMultiDay,
} from '../../models/Bookable'
import { Booking } from '../../models/Booking'
import { Lesson } from '../../models/Lesson'
import { PSEvent } from '../../models/PSEvent'
import { Payee } from '../../models/Payee'
import { isPast } from '../../utils/dates'
import { Icon } from '../Icon'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { ContactAvatar } from '../contacts/ContactAvatar'

type Props = {
    bookable: Bookable
    day: moment.Moment
    payees: Payee[]
    size?: 'xs' | 'sm' | 'md' | 'lg'
    style?: React.CSSProperties
    showStatus?: boolean
}

export const CalendarBookableCard = ({
    bookable,
    day,
    payees,
    size = 'sm',
    style = {},
    showStatus = true,
}: Props) => {
    const navigate = usePSNavigate()

    const windowSize = useWindowResize()
    const cardRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const leftContentRef = useRef<HTMLDivElement>(null)
    const rightContentRef = useRef<HTMLDivElement>(null)

    const [hideStatus, setHideStatus] = useState(false)

    const [title, secondaryTitle] = getBookableTitles(bookable)
    const timeLabel = getBookableTimeLabel(bookable, day)
    const multiDayLabel = getBookableMultiDayLabel(bookable, day)
    const color = getBookableColor(bookable, payees)
    const bookableStatus = getBookableStatus(bookable)

    const { data: subtitle } = useQuery({
        queryKey: ['calendar', 'bookables', 'subtitle', bookable.id],
        queryFn: async () => (await getBookableSubtitle(bookable)) || '',
    })

    useLayoutEffect(() => {
        if (!style || !windowSize) return
        if (
            !cardRef.current ||
            !contentRef.current ||
            !leftContentRef.current ||
            !rightContentRef.current
        )
            return

        const ROUNDING_EPSILON = 1
        const CONTENT_PADDING_X = 16
        const CONTENT_PADDING_Y = size === 'xs' ? 0 : size === 'sm' ? 4 : 12
        const CONTENT_GAP = 4

        // Check if the right side content (i.e. status text) wraps under the left side content (i.e. title, subtitle, time, etc.)
        // We use an epsilon of 1 pixel to nudge the comparison in favor of the content wrapping (i.e. assume it wrapped if it's a tight measure)
        const contentWidth = contentRef.current.clientWidth - CONTENT_PADDING_X
        const contentHeight = contentRef.current.clientHeight - CONTENT_PADDING_Y
        const contentRightWraps =
            contentWidth - ROUNDING_EPSILON <=
            leftContentRef.current.clientWidth + CONTENT_GAP + rightContentRef.current.clientWidth

        // Subsequently check if the content overlflows the height
        // Again we use the epsilon to favor an overflow
        const contentOverflows =
            contentHeight + ROUNDING_EPSILON >= cardRef.current.clientHeight - CONTENT_GAP

        // Hide the status text if it wraps to a new line and overflows the height of the container
        // Note that the rounding epsilon ensures we favor hiding the status over showing it
        setHideStatus(contentRightWraps && contentOverflows)
    }, [style, windowSize, size])

    const handleClick = useCallback(() => {
        if (bookable instanceof Booking) navigate(`/bookings/${bookable.id}`)
        else if (bookable instanceof Lesson) navigate(`/lessons/${bookable.id}`)
        else if (bookable instanceof Block) navigate(`/blocks/${bookable.id}/edit`)
        else if (bookable instanceof PSEvent && bookable.ownerIsMe)
            navigate(`/events/${bookable.id}`)
        else if (isFixedEventReservationItem(bookable)) navigate(`/reservations/${bookable.id}`)
    }, [bookable])

    const sizeClass = useMemo(() => {
        switch (size) {
            case 'xs':
                return 'py-0'
            case 'sm':
                return 'py-[2px]'
            case 'md':
            case 'lg':
            default:
                return 'py-[6px]'
        }
    }, [size])

    return (
        <div
            ref={cardRef}
            className={clsx(
                'flex min-h-[12px] min-w-[70px] shrink-0 overflow-hidden rounded-[4px]',
                !(bookable instanceof PSEvent) || bookable.ownerIsMe
                    ? 'hover:cursor-pointer hover:bg-current hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 active:bg-current active:bg-linear-to-b active:from-black/8 active:to-black/8'
                    : 'hover:cursor-default'
            )}
            style={{
                backgroundColor: color.bg,
                opacity: isPast(bookable.endDate) ? 0.6 : 1,
                ...style,
            }}
            onClick={handleClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick()
            }}
            tabIndex={0}
        >
            <div
                className="w-1 min-w-1 self-stretch rounded-[9px]"
                style={{ background: color.fg }}
            ></div>

            <div
                className={twMerge(
                    'flex min-w-0 grow flex-wrap justify-between gap-1 self-start px-2',
                    sizeClass
                )}
                ref={contentRef}
            >
                <div className="flex min-w-0 flex-col gap-1" ref={leftContentRef}>
                    <div className="flex items-center gap-2">
                        {bookable.person?.id && size !== 'xs' && (
                            <ContactAvatar
                                user={bookable.subaccount ?? bookable.person}
                                size="xs"
                                className="border-2 border-white"
                            />
                        )}
                        <Label
                            size="xs"
                            className={`pointer-events-none truncate text-gray-700 ${size === 'xs' ? 'leading-3' : 'leading-6'}`}
                        >
                            {title}
                            {secondaryTitle !== undefined && (
                                <span className="ml-1 font-light text-gray-600">
                                    {secondaryTitle}
                                </span>
                            )}
                            {['xs', 'sm'].includes(size) && (
                                <span className="font-normal text-gray-600">
                                    {!!subtitle && ` · ${subtitle} `}
                                    {isMultiDay(bookable) && (
                                        <span className="font-bold"> {multiDayLabel}</span>
                                    )}
                                    {!!timeLabel && ` · ${timeLabel}`}
                                </span>
                            )}
                        </Label>
                    </div>
                    {size == 'md' && (
                        <div className="flex items-center gap-3">
                            <Paragraph size="xs" className="truncate text-gray-600">
                                {subtitle}
                                {isMultiDay(bookable) && (
                                    <span className="font-bold"> {multiDayLabel}</span>
                                )}
                                {!!subtitle && !!timeLabel && ` · ${timeLabel}`}
                                {!subtitle && timeLabel}
                            </Paragraph>
                            {bookable.isRecurring && (
                                <Icon icon="repeat.small" className="shrink-0 text-gray-600" />
                            )}
                        </div>
                    )}
                    {size === 'lg' && (
                        <>
                            <Paragraph size="xs" className="truncate text-gray-600">
                                {subtitle}
                                {isMultiDay(bookable) && (
                                    <span className="font-bold"> {multiDayLabel}</span>
                                )}
                            </Paragraph>
                            <div className="flex items-center gap-3">
                                <Paragraph size="xs" className="truncate text-gray-600">
                                    {timeLabel}
                                </Paragraph>
                                {bookable.isRecurring && (
                                    <Icon icon="repeat.small" className="shrink-0 text-gray-600" />
                                )}
                            </div>
                        </>
                    )}
                </div>
                {showStatus && !!bookableStatus.status && (
                    <div className="inline-flex items-start" ref={rightContentRef}>
                        <Label
                            size="xs"
                            className={`pointer-events-none capitalize ${size === 'xs' ? 'leading-3' : size === 'sm' ? 'leading-6' : 'leading-4'}`}
                            style={{
                                color: bookableStatus.color.fg,
                                visibility: hideStatus ? 'hidden' : 'visible',
                            }}
                        >
                            {bookableStatus.status}
                        </Label>
                    </div>
                )}
            </div>
        </div>
    )
}
